import { useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import type { TrackedProduct } from "@/lib/types";

export function TrackedProducts({
  initialProducts,
}: {
  initialProducts: TrackedProduct[];
}) {
  const [products, setProducts] = useState<TrackedProduct[]>(initialProducts);
  const [newId, setNewId] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function addProduct(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setAdding(true);
    try {
      const res = await fetch("/api/stripe/products", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_id: newId.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Failed to add product");
      setProducts((p) => [
        ...p.filter(
          (x) => x.stripe_product_id !== data.product.stripe_product_id
        ),
        data.product,
      ]);
      setNewId("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add product");
    } finally {
      setAdding(false);
    }
  }

  async function removeProduct(productId: string) {
    const res = await fetch(
      `/api/stripe/products?product_id=${encodeURIComponent(productId)}`,
      { method: "DELETE", credentials: "include" }
    );
    if (res.ok) {
      setProducts((p) => p.filter((x) => x.stripe_product_id !== productId));
    }
  }

  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold">Tracked Products</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Stripe products counted toward MRR, client, and agency metrics.
        </p>
      </div>

      <div className="mb-4 space-y-2">
        {products.length === 0 ? (
          <div className="rounded-md border border-dashed bg-slate-50 px-4 py-6 text-center text-sm text-muted-foreground">
            No tracked products yet.
          </div>
        ) : (
          products.map((product) => (
            <div
              key={product.stripe_product_id}
              className="flex items-center justify-between rounded-md border bg-slate-50 px-4 py-2.5"
            >
              <div>
                <div className="text-sm font-medium">
                  {product.product_name ?? "Unnamed product"}
                </div>
                <div className="font-mono text-xs text-muted-foreground">
                  {product.stripe_product_id}
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeProduct(product.stripe_product_id)}
                className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                title="Remove"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))
        )}
      </div>

      <form onSubmit={addProduct} className="flex gap-2">
        <input
          type="text"
          placeholder="prod_..."
          value={newId}
          onChange={(e) => setNewId(e.target.value)}
          className="flex-1 rounded-md border px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-ring"
        />
        <button
          type="submit"
          disabled={adding || !newId.trim()}
          className="flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {adding ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Plus className="h-4 w-4" />
          )}
          Add
        </button>
      </form>

      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
    </div>
  );
}
